import { useEffect, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  DataTable,
  ErrorState,
  LaporanTampil,
  PageHeader,
  Select,
  SkeletonKartu,
  StatCard,
  Tabs,
  useToast,
  type ItemTab,
  type KolomTabel,
} from '@/components'
import { api, buatIdempotencyKey, pesanKesalahan } from '@/lib/api'
import { cariAngka } from '@/components/LaporanTampil'
import { formatRupiah, formatTanggal } from '@/lib/format'
import type { Santri, Tagihan } from '@/lib/types'
import { useApi } from '@/lib/useApi'

interface AnakWali {
  studentId: string
  nama?: string
  name?: string
  studentNumber?: string
  unitName?: string
  relationshipType?: string
  canViewAcademic?: boolean
  canViewAttendance?: boolean
  canViewBilling?: boolean
  canPay?: boolean
  [kunci: string]: unknown
}

const TAB: ItemTab[] = [
  { id: 'dashboard', label: 'Ringkasan' },
  { id: 'tagihan', label: 'Tagihan' },
  { id: 'pembayaran', label: 'Riwayat Pembayaran' },
  { id: 'absensi', label: 'Absensi' },
  { id: 'nilai', label: 'Nilai' },
  { id: 'pengumuman', label: 'Pengumuman' },
]

export default function PortalWali() {
  const toast = useToast()
  const [anakDipilih, setAnakDipilih] = useState('')
  const [tab, setTab] = useState('dashboard')
  const [mengajukan, setMengajukan] = useState<string | null>(null)

  const anak = useApi<unknown>('/portal/children', {})
  const anakWali: AnakWali[] = Array.isArray(anak.data)
    ? (anak.data as AnakWali[])
    : ((anak.data as { items?: AnakWali[] } | null)?.items ?? [])

  useEffect(() => {
    if (!anakDipilih && anakWali.length > 0) {
      setAnakDipilih(String(anakWali[0].studentId ?? anakWali[0].id ?? ''))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anak.data])

  const dashboard = useApi<unknown>(tab === 'dashboard' ? '/portal/dashboard' : null, {
    studentId: anakDipilih || undefined,
  })
  const tagihan = useApi<unknown>(tab === 'tagihan' ? `/portal/children/${anakDipilih}/invoices` : null, {})
  const pembayaran = useApi<unknown>('/portal/children/' + anakDipilih + '/payments', {}, { tunda: tab !== 'pembayaran' })
  const absensi = useApi<unknown>('/portal/children/' + anakDipilih + '/attendance', {}, { tunda: tab !== 'absensi' })
  const nilai = useApi<unknown>('/portal/children/' + anakDipilih + '/grades', {}, { tunda: tab !== 'nilai' })
  const pengumuman = useApi<unknown>('/portal/announcements', {}, { tunda: tab !== 'pengumuman' })

  const daftarTagihan: Tagihan[] = Array.isArray(tagihan.data)
    ? (tagihan.data as Tagihan[])
    : ((tagihan.data as { items?: Tagihan[] } | null)?.items ?? [])

  const kolomTagihan: KolomTabel<Tagihan>[] = [
    { kunci: 'invoiceNumber', judul: 'Nomor Tagihan' },
    { kunci: 'invoiceDate', judul: 'Tanggal', render: (b) => formatTanggal(b.invoiceDate) },
    { kunci: 'dueDate', judul: 'Jatuh Tempo', render: (b) => formatTanggal(b.dueDate) },
    { kunci: 'totalAmount', judul: 'Total', rata: 'kanan', render: (b) => formatRupiah(b.totalAmount) },
    { kunci: 'paidAmount', judul: 'Dibayar', rata: 'kanan', render: (b) => formatRupiah(b.paidAmount) },
    {
      kunci: 'outstandingAmount',
      judul: 'Sisa',
      rata: 'kanan',
      render: (b) => <span className="font-medium text-red-700">{formatRupiah(b.outstandingAmount)}</span>,
    },
    { kunci: 'status', judul: 'Status', render: (b) => <Badge nada={b.outstandingAmount > 0 ? 'peringatan' : 'sukses'}>{b.status}</Badge> },
    {
      kunci: 'aksi',
      judul: 'Aksi',
      sortable: false,
      render: (b) => (
        <div className="flex flex-wrap gap-1.5">
          <Button
            ukuran="kecil"
            varian="garis"
            onClick={async () => {
              try {
                const res = await fetch(`/api/invoices/${b.id}/receipt`, { credentials: 'include' })
                if (!res.ok) throw new Error('Kuitansi belum tersedia.')
                const blob = await res.blob()
                window.open(URL.createObjectURL(blob), '_blank')
              } catch (e) {
                toast.gagal(pesanKesalahan(e))
              }
            }}
          >
            Kuitansi
          </Button>
          {b.outstandingAmount > 0 ? (
            <Button
              ukuran="kecil"
              memuat={mengajukan === b.id}
              onClick={async () => {
                setMengajukan(b.id)
                try {
                  await api.post(
                    '/portal/payment-intents',
                    {
                      studentId: anakDipilih,
                      amount: b.outstandingAmount,
                      invoiceIds: [b.id],
                    },
                    { idempotencyKey: buatIdempotencyKey() },
                  )
                  toast.sukses('Permintaan pembayaran dibuat. Silakan lanjutkan pembayaran.')
                  tagihan.muat()
                } catch (e) {
                  toast.gagal(pesanKesalahan(e))
                } finally {
                  setMengajukan(null)
                }
              }}
            >
              Bayar
            </Button>
          ) : null}
        </div>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        judul="Portal Wali Santri"
        deskripsi="Pantau tagihan, pembayaran, absensi, nilai, dan pengumuman anak Anda."
        aksi={
          <div className="min-w-56">
            <Select
              label="Pilih Anak"
              placeholder={anak.memuat ? 'Memuat…' : anakWali.length === 0 ? 'Tidak ada data anak' : 'Pilih anak'}
              opsi={anakWali.map((a) => ({
                value: String(a.studentId),
                label: `${a.nama ?? a.name ?? a.studentId}${a.studentNumber ? ` (${a.studentNumber})` : ''}`,
              }))}
              value={anakDipilih}
              onChange={(e) => setAnakDipilih(e.target.value)}
            />
          </div>
        }
      />

      {anak.galat ? (
        <div className="mb-4">
          <ErrorState
            pesan={anak.galat}
            kode={anak.kodeGalat}
            onCoba={anak.muat}
            ringkas
          />
        </div>
      ) : null}

      {anakWali.length === 0 && !anak.memuat && !anak.galat ? (
        <Card>
          <p className="py-6 text-center text-sm text-netral-500">
            Akun Anda belum terhubung ke data santri. Hubungi administrator untuk verifikasi wali.
          </p>
        </Card>
      ) : null}

      {anakWali.length > 0 ? (
        <>
          <Tabs tab={TAB} aktif={tab} onUbah={setTab} />

          {tab === 'dashboard' ? (
            <div className="space-y-4">
              {dashboard.memuat && !dashboard.data ? (
                <SkeletonKartu jumlah={4} />
              ) : dashboard.galat ? (
                <ErrorState pesan={dashboard.galat} kode={dashboard.kodeGalat} onCoba={dashboard.muat} />
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <StatCard
                      label="Total Tunggakan"
                      nilai={cariAngka(dashboard.data, ['totalTunggakan', 'tunggakan', 'totalOutstanding'])}
                      nada="merah"
                      formatNilai={(v) => formatRupiah(Number(v ?? 0))}
                    />
                    <StatCard
                      label="Total Dibayar"
                      nilai={cariAngka(dashboard.data, ['totalDibayar', 'totalPaid', 'sudahDibayar'])}
                      nada="hijau"
                      formatNilai={(v) => formatRupiah(Number(v ?? 0))}
                    />
                    <StatCard
                      label="Kehadiran"
                      nilai={cariAngka(dashboard.data, ['persentaseKehadiran', 'kehadiran', 'attendanceRate'])}
                      formatNilai={(v) => (v === null || v === undefined ? '—' : `${Number(v)}%`)}
                    />
                    <StatCard label="Jumlah Tagihan Aktif" nilai={cariAngka(dashboard.data, ['jumlahTagihan', 'tagihanAktif'])} />
                  </div>
                  <LaporanTampil
                    data={dashboard.data}
                    memuat={false}
                    galat={null}
                    kosong={<p className="py-4 text-center text-sm text-netral-500">Ringkasan belum tersedia.</p>}
                  />
                </>
              )}
            </div>
          ) : null}

          {tab === 'tagihan' ? (
            <Card padat className="overflow-hidden">
              <div className="border-b border-netral-200 px-4 py-2.5">
                <CardHeader judul="Tagihan Anak" deskripsi="Klik Bayar untuk membuat permintaan pembayaran." />
              </div>
              <DataTable
                kolom={kolomTagihan}
                data={daftarTagihan}
                kunciBaris={(b) => b.id}
                memuat={tagihan.memuat}
                galat={tagihan.galat}
                kodeGalat={tagihan.kodeGalat}
                onCoba={tagihan.muat}
                pesanKosong="Tidak ada tagihan untuk anak ini."
              />
            </Card>
          ) : null}

          {tab === 'pembayaran' ? (
            <LaporanTampil
              data={pembayaran.data}
              memuat={pembayaran.memuat}
              galat={pembayaran.galat}
              kodeGalat={pembayaran.kodeGalat}
              onCoba={pembayaran.muat}
              kosong={<p className="py-6 text-center text-sm text-netral-500">Belum ada riwayat pembayaran.</p>}
            />
          ) : null}

          {tab === 'absensi' ? (
            <LaporanTampil
              data={absensi.data}
              memuat={absensi.memuat}
              galat={absensi.galat}
              kodeGalat={absensi.kodeGalat}
              onCoba={absensi.muat}
              kosong={<p className="py-6 text-center text-sm text-netral-500">Belum ada data absensi.</p>}
            />
          ) : null}

          {tab === 'nilai' ? (
            <LaporanTampil
              data={nilai.data}
              memuat={nilai.memuat}
              galat={nilai.galat}
              kodeGalat={nilai.kodeGalat}
              onCoba={nilai.muat}
              kosong={<p className="py-6 text-center text-sm text-netral-500">Belum ada data nilai.</p>}
            />
          ) : null}

          {tab === 'pengumuman' ? (
            <LaporanTampil
              data={pengumuman.data}
              memuat={pengumuman.memuat}
              galat={pengumuman.galat}
              kodeGalat={pengumuman.kodeGalat}
              onCoba={pengumuman.muat}
              kosong={<p className="py-6 text-center text-sm text-netral-500">Belum ada pengumuman.</p>}
            />
          ) : null}
        </>
      ) : null}
    </div>
  )
}

export type { AnakWali as PortalAnak, Santri as PortalSantri }
